import { guestApiClient } from './apiClient';
import { BillSummary, PaymentRequest, PaymentResult, ApiResponse } from '../types';
import { formatCurrency, formatCurrencyWithCode, getDefaultCurrency } from '../utils/currency';
import { authService } from './authService';

export interface PaymentReceiptResponse {
  payment_id: string;
  transaction_id: string;
  authorization_code: string;
  amount: number;
  payment_method: string;
  date: string;
  status: string;
  guest_name: string;
  reservation_id: string;
  hotel: {
    name: string;
    address: string;
    phone: string;
  };
  card_info?: {
    last_digits: string;
    card_type: string;
    cardholder_name: string;
  };
}

class BillingService {
  private hotelCurrency: string | null = null;

  /**
   * Get hotel currency from session (cached)
   */
  private async getHotelCurrency(): Promise<string> {
    if (this.hotelCurrency) {
      return this.hotelCurrency;
    }

    try {
      const sessionResponse = await authService.validateSession();
      this.hotelCurrency = sessionResponse?.data?.booking?.hotel?.currency || getDefaultCurrency();
      return this.hotelCurrency!;
    } catch (error) {
      return getDefaultCurrency();
    }
  }

  /**
   * Reset cached currency (call when switching hotels/sessions)
   */
  resetCurrency(): void {
    this.hotelCurrency = null;
  }

  /**
   * Get guest's current bill summary with all charges and payments.
   *
   * guestApiClient already wraps the axios body as {success, data: body},
   * and the backend body is itself {success, data: payload} — so unwrap
   * both layers here and hand callers {success, data: payload} directly.
   * (Reading response.data in a component otherwise renders the envelope,
   * not the bill — that's the toFixed-on-undefined crash.)
   */
  async getBillSummary(): Promise<ApiResponse<BillSummary>> {
    const response = await guestApiClient.get<{ success: boolean; data: BillSummary }>('/api/guest/bill/');
    return {
      success: Boolean(response.data?.success),
      data: response.data?.data as BillSummary,
    };
  }

  /**
   * Process a payment for the guest's bill
   */
  async processPayment(paymentRequest: PaymentRequest): Promise<ApiResponse<PaymentResult>> {
    const response = await guestApiClient.post<{ success: boolean; data: PaymentResult }>('/api/guest/payments/', paymentRequest);
    return {
      success: Boolean(response.data?.success),
      data: response.data?.data as PaymentResult,
    };
  }

  /**
   * Get payment receipt
   */
  async getPaymentReceipt(paymentId: string): Promise<ApiResponse<PaymentReceiptResponse>> {
    const response = await guestApiClient.get<{ success: boolean; data: PaymentReceiptResponse }>(`/api/guest/payments/${paymentId}/receipt/`);
    return {
      success: Boolean(response.data?.success),
      data: response.data?.data as PaymentReceiptResponse,
    };
  }

  /**
   * Validate payment amount against current balance
   */
  validatePaymentAmount(amount: number, maxAmount: number, currencyCode?: string): { valid: boolean; message?: string } {
    if (amount <= 0) {
      return { valid: false, message: 'Payment amount must be greater than zero' };
    }

    const currency = currencyCode || getDefaultCurrency();
    if (amount > maxAmount) {
      return { valid: false, message: `Amount cannot exceed outstanding balance of ${formatCurrency(maxAmount, currency)}` };
    }

    return { valid: true };
  }

  /**
   * Format currency amount for display using hotel's currency
   */
  formatAmount(amount: number, currencyCode?: string): string {
    const currency = currencyCode || this.hotelCurrency || getDefaultCurrency();
    return formatCurrency(amount, currency);
  }

  /**
   * Format currency amount with currency code
   */
  formatAmountWithCode(amount: number, currencyCode?: string): string {
    const currency = currencyCode || this.hotelCurrency || getDefaultCurrency();
    return formatCurrencyWithCode(amount, currency);
  }

  /**
   * Format currency amount with auto-detected hotel currency (async version)
   */
  async formatAmountAsync(amount: number, currencyCode?: string): Promise<string> {
    const currency = currencyCode || await this.getHotelCurrency();
    return formatCurrency(amount, currency);
  }

  /**
   * Format currency amount with currency code (async version)
   */
  async formatAmountWithCodeAsync(amount: number, currencyCode?: string): Promise<string> {
    const currency = currencyCode || await this.getHotelCurrency();
    return formatCurrencyWithCode(amount, currency);
  }

  /**
   * Get payment method display name
   */
  getPaymentMethodDisplay(method: string): string {
    const methodMap: Record<string, string> = {
      'credit_card': 'Credit Card',
      'debit_card': 'Debit Card',
      'cash': 'Cash',
      'upi_transfer': 'UPI Transfer',
      'bank_transfer': 'Bank Transfer'
    };
    return methodMap[method] || method;
  }

  /**
   * Validate credit card number (basic Luhn algorithm)
   */
  validateCardNumber(cardNumber: string): boolean {
    // Remove spaces and non-digits
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    // Check length
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      return false;
    }

    // Luhn algorithm
    let sum = 0;
    let isEven = false;

    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  /**
   * Validate expiry date
   */
  validateExpiryDate(month: string, year: string): boolean {
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (monthNum < 1 || monthNum > 12) {
      return false;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
    const currentMonth = currentDate.getMonth() + 1;

    if (yearNum < currentYear) {
      return false;
    }

    if (yearNum === currentYear && monthNum < currentMonth) {
      return false;
    }

    return true;
  }

  /**
   * Validate CVV
   */
  validateCVV(cvv: string): boolean {
    return /^\d{3,4}$/.test(cvv);
  }

  /**
   * Format card number for display (mask all but last 4 digits)
   */
  formatCardNumberForDisplay(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    if (cleanNumber.length < 4) return cardNumber;
    
    const lastFour = cleanNumber.slice(-4);
    const masked = '**** **** **** ';
    return masked + lastFour;
  }

  /**
   * Get card type from card number
   */
  getCardType(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (/^4/.test(cleanNumber)) return 'Visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'Mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'American Express';
    if (/^6/.test(cleanNumber)) return 'Discover';
    
    return 'Unknown';
  }

  /**
   * Calculate payment suggestion based on bill amount
   */
  getPaymentSuggestions(totalAmount: number, currencyCode?: string): number[] {
    const currency = currencyCode || getDefaultCurrency();
    
    // Currency-appropriate minimum amounts
    const minimumAmounts: Record<string, number> = {
      USD: 50,   // $50
      EUR: 45,   // €45
      GBP: 40,   // £40
      INR: 500,  // ₹500
      JPY: 5000, // ¥5000
      CNY: 300,  // ¥300
      CAD: 65,   // C$65
      AUD: 70,   // A$70
      CHF: 50,   // CHF50
      SEK: 500,  // 500kr
      NOK: 500,  // 500kr
      DKK: 350,  // 350kr
      // Add more as needed
    };
    
    const minimumAmount = minimumAmounts[currency.toUpperCase()] || 50;
    
    const suggestions = [
      totalAmount, // Full amount
      totalAmount * 0.75, // 75%
      totalAmount * 0.5, // 50%
      Math.max(minimumAmount, totalAmount * 0.25) // 25% or currency-appropriate minimum
    ];

    return suggestions
      .map(amount => Math.round(amount * 100) / 100) // Round to 2 decimal places
      .filter((amount, index, arr) => arr.indexOf(amount) === index) // Remove duplicates
      .sort((a, b) => b - a); // Sort descending
  }
}

export const billingService = new BillingService();